import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Badge, Box, Avatar, Typography, Menu, MenuItem, IconButton, Divider } from '@mui/material';
import { Link, useRouter, useLocation } from '@tanstack/react-router';
import { useSnackbar } from 'notistack';
import { useState, useMemo } from 'react';

import { useSubscriptionSummaryQuery } from 'entities/subject-change-event';
import { canUseTrackedSubjects, getUserInitials, useCurrentUserQuery, UserRole } from 'entities/user';
import { useLogoutMutation } from 'features/auth';
import { Routes } from 'shared/routes';

import { styles } from './styles';

const ClientHeader: React.FC = () => {
  const { data: currentUser } = useCurrentUserQuery();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const router = useRouter();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [logout] = useLogoutMutation();

  const initials = getUserInitials(currentUser);
  const userName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : 'User';
  const companyName = currentUser?.account?.sourceOrganization?.name?.trim() || null;
  const hasAdminAccess = currentUser?.role === UserRole.Admin || currentUser?.role === UserRole.Editor;
  const trackedSubjectsEnabled = canUseTrackedSubjects(currentUser);
  const { data: subscriptionSummary } = useSubscriptionSummaryQuery(undefined, { skip: !trackedSubjectsEnabled });
  const unreadEvents = subscriptionSummary?.unreadEvents ?? 0;
  const homePath = Routes.frontend.rootPath;

  const tabs = useMemo(
    () =>
      [
        { id: 'search', label: 'Search', path: Routes.frontend.searchPath },
        { id: 'relationships', label: 'Relationships', path: Routes.frontend.relationshipsPath },
        trackedSubjectsEnabled
          ? { id: 'subscriptions', label: 'My EIQ', path: Routes.frontend.subscriptionsPath }
          : null,
        { id: 'profile-requests', label: 'Request a Profile', path: Routes.frontend.profileRequestsPath },
      ].filter(Boolean) as Array<{ id: string; label: string; path: string }>,
    [trackedSubjectsEnabled],
  );

  const handleUserClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleUserClose = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    handleUserClose();
    try {
      await logout().unwrap();
      router.navigate({ to: Routes.frontend.loginPath });
    } catch {
      enqueueSnackbar('Logout failed. Please try again.', { variant: 'error' });
    }
  };

  const activeTab = useMemo(() => {
    const currentPath = location.pathname;
    if (currentPath === Routes.frontend.profileRequestsPath) return 'profile-requests';
    if (currentPath === Routes.frontend.relationshipsPath) return 'relationships';
    if (currentPath === Routes.frontend.searchPath) return 'search';
    if (
      currentPath === Routes.frontend.subscriptionsPath ||
      currentPath.startsWith('/subscriptions/tracked-subjects')
    ) {
      return 'subscriptions';
    }
    return 'search';
  }, [location.pathname]);

  const handleTabClick = (tab: { id: string; path: string }) => {
    router.navigate({ to: tab.path });
  };

  return (
    <Box sx={styles.headerOuter}>
      <Box component="header" sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <Box component={Link} to={homePath} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src="/logo.svg" alt="ExecutiveIQ" style={{ height: '36px', width: '141px' }} />
          </Box>
        </Box>
        <Box sx={styles.headerCenter}>
          {tabs.map((tab) => (
            <Box
              key={tab.id}
              sx={[
                styles.tab,
                activeTab === tab.id && styles.tabActive,
                !tab.path && { opacity: 0.5, cursor: 'not-allowed' },
              ]}
              onClick={() => handleTabClick(tab)}
            >
              <Typography sx={activeTab === tab.id ? styles.tabText : styles.tabTextInactive}>{tab.label}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={styles.headerRight}>
          {trackedSubjectsEnabled && (
            <IconButton
              onClick={() => router.navigate({ to: Routes.frontend.subscriptionsPath })}
              aria-label="Dashboard notifications"
            >
              <Badge badgeContent={unreadEvents} color="error" invisible={unreadEvents === 0}>
                <NotificationsOutlinedIcon />
              </Badge>
            </IconButton>
          )}
          <IconButton onClick={handleUserClick} sx={styles.userButton}>
            <Avatar sx={styles.userAvatar}>{initials || '?'}</Avatar>
            <Typography sx={styles.userName}>{userName}</Typography>
          </IconButton>
          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleUserClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                sx: styles.menuPaper,
              },
            }}
          >
            {companyName && (
              <Box sx={styles.menuCompany}>
                <Typography sx={styles.menuCompanyLabel}>Company</Typography>
                {currentUser?.account?.sourceOrganization?.id ? (
                  <Typography
                    component={Link}
                    to={Routes.frontend.organizationPath(currentUser.account.sourceOrganization.id)}
                    onClick={handleUserClose}
                    sx={styles.menuCompanyLink}
                  >
                    {companyName}
                  </Typography>
                ) : (
                  <Typography sx={styles.menuCompanyName}>{companyName}</Typography>
                )}
              </Box>
            )}
            {companyName && <Divider sx={styles.menuDivider} />}
            <MenuItem component={Link} to={Routes.frontend.userProfilePath} onClick={handleUserClose}>
              <PersonOutlineIcon sx={{ marginRight: '8px', fontSize: '20px' }} />
              Profile
            </MenuItem>
            {hasAdminAccess && (
              <MenuItem component={Link} to={Routes.frontend.adminPath} onClick={handleUserClose}>
                <AdminPanelSettingsIcon sx={{ marginRight: '8px', fontSize: '20px' }} />
                Admin Panel
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ marginRight: '8px', fontSize: '20px' }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export { ClientHeader };
